import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { PortfolioManager, type Portfolio } from '../../services/portfolioManager';

interface PortfolioSelectorProps {
  activePortfolio: Portfolio | null;
  onPortfolioChange: (portfolio: Portfolio) => void;
  onCreatePortfolio: () => void;
  onDeletePortfolio?: (portfolio: Portfolio) => void;
}

/**
 * PortfolioSelector Component
 * Dropdown pour sélectionner et gérer les portfolios
 */
export function PortfolioSelector({
  activePortfolio,
  onPortfolioChange,
  onCreatePortfolio,
  onDeletePortfolio
}: PortfolioSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<Portfolio | null>(null);
  const portfolios = PortfolioManager.getAllPortfolios() || [];

  const handleDeleteClick = (e: React.MouseEvent, portfolio: Portfolio) => {
    e.stopPropagation();
    setPortfolioToDelete(portfolio);
  };

  const confirmDelete = () => {
    if (portfolioToDelete) {
      const success = PortfolioManager.deletePortfolio(portfolioToDelete.id);
      if (success) {
        // Si c'était le portfolio actif, on switch vers le premier disponible
        if (activePortfolio?.id === portfolioToDelete.id) {
          const remaining = PortfolioManager.getAllPortfolios();
          if (remaining.length > 0) {
            onPortfolioChange(remaining[0]);
          }
        }
        onDeletePortfolio?.(portfolioToDelete);
      }
      setPortfolioToDelete(null);
    }
  };

  const cancelDelete = () => {
    setPortfolioToDelete(null);
  };

  const isDefaultPortfolio = (id: string) => id === 'default';

  const handleSelect = (portfolio: Portfolio) => {
    onPortfolioChange(portfolio);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Bouton de sélection - design unifié */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minWidth: '180px',
          justifyContent: 'space-between',
          padding: '8px 14px',
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: isOpen ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.background = 'var(--bg-secondary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isOpen ? 'var(--primary)' : 'var(--border)';
          e.currentTarget.style.background = 'var(--card-bg)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              fontSize: '16px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${activePortfolio?.color || '#6366f1'}20, ${activePortfolio?.color || '#6366f1'}10)`,
              borderRadius: '8px',
            }}
          >
            {activePortfolio?.icon || '💼'}
          </span>
          <span style={{
            fontWeight: 500,
            fontSize: '14px',
            color: 'var(--text)',
          }}>
            {activePortfolio?.name || 'Sélectionner'}
          </span>
        </div>
        <Icons.ChevronDown
          size={16}
          style={{
            color: 'var(--muted)',
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Overlay pour fermer en cliquant à l'extérieur */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div
            className="card"
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              minWidth: '280px',
              maxWidth: '400px',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 1000,
              padding: '8px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}
          >
            {/* Portfolios list */}
            <div style={{ marginBottom: '8px' }}>
              {portfolios.map((portfolio) => (
                <div
                  key={portfolio.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginBottom: '4px'
                  }}
                >
                  <button
                    onClick={() => handleSelect(portfolio)}
                    className="btn-ghost"
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      backgroundColor:
                        activePortfolio?.id === portfolio.id
                          ? 'var(--bg-secondary)'
                          : 'transparent',
                      borderLeft:
                        activePortfolio?.id === portfolio.id
                          ? `3px solid ${portfolio.color || '#6366f1'}`
                          : '3px solid transparent'
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{portfolio.icon || '💼'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                        {portfolio.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                        {(portfolio.tokens || []).length} token{(portfolio.tokens || []).length > 1 ? 's' : ''}
                        {portfolio.description && (
                          <> • {portfolio.description}</>
                        )}
                      </div>
                    </div>
                    {activePortfolio?.id === portfolio.id && (
                      <Icons.Check size={18} style={{ color: portfolio.color || '#6366f1' }} />
                    )}
                  </button>
                  {/* Delete button - only for non-default portfolios */}
                  {!isDefaultPortfolio(portfolio.id) && (
                    <button
                      onClick={(e) => handleDeleteClick(e, portfolio)}
                      className="btn-ghost"
                      style={{
                        padding: '8px',
                        color: 'var(--muted)',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--danger)';
                        e.currentTarget.style.backgroundColor = 'var(--danger-bg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--muted)';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      title={t('portfolioSelector.deletePortfolio')}
                    >
                      <Icons.Trash size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0' }} />

            {/* Nouveau portfolio button */}
            <button
              onClick={() => {
                setIsOpen(false);
                onCreatePortfolio();
              }}
              className="btn-ghost"
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'var(--primary)',
                fontWeight: 600
              }}
            >
              <Icons.Plus size={20} />
              <span>Créer un nouveau portfolio</span>
            </button>

            {/* Info */}
            <div style={{
              marginTop: '8px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary)',
              fontSize: '11px',
              color: 'var(--muted)'
            }}>
              <Icons.Info size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {portfolios.length} portfolio{portfolios.length > 1 ? 's' : ''} •
              {' '}{portfolios.reduce((sum, p) => sum + (p.tokens || []).length, 0)} tokens total
            </div>
          </div>
        </>
      )}

      {/* Delete confirmation modal */}
      {portfolioToDelete && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={cancelDelete}
          >
            <div
              className="card"
              style={{
                padding: '24px',
                maxWidth: '400px',
                width: '90%',
                textAlign: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: 'var(--danger-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Icons.Trash size={28} style={{ color: 'var(--danger)' }} />
              </div>

              <h3 style={{ marginBottom: '8px', fontSize: '18px' }}>
                {t('portfolioSelector.confirmDelete.title')}
              </h3>
              <p style={{ color: 'var(--muted)', marginBottom: '8px', fontSize: '14px' }}>
                {t('portfolioSelector.confirmDelete.message', { name: portfolioToDelete.name })}
              </p>
              <p style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '20px' }}>
                {t('portfolioSelector.confirmDelete.warning', { count: portfolioToDelete.tokens?.length || 0 })}
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={cancelDelete}
                  className="btn-ghost"
                  style={{ padding: '10px 20px' }}
                >
                  {t('portfolioSelector.confirmDelete.cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'var(--danger)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {t('portfolioSelector.confirmDelete.confirm')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
